import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useScroll, useTransform } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { ArrowRight } from 'lucide-react';
import { boardsStore, generateBoardId } from '../stores/boardsStore';
import { getEmployeesArray } from '../employees/employeesStore';
import { useSeoMeta } from '../hooks/useSeoMeta';
import { LandingTopNav } from './LandingTopNav';
import { LandingHero } from './LandingHero';
import { LandingFeatureShowcase } from './LandingFeatureShowcase';
import { LandingWorkflowSection } from './LandingWorkflowSection';
import { LandingAudienceSection } from './LandingAudienceSection';
import { LandingDemo } from './LandingDemo';

const midCtaText = {
  de: { heading: 'Direkt loslegen.', sub: 'Kein Account, keine Cloud. Alle Daten bleiben lokal im Browser.', board: 'Board erstellen', canvas: 'Canvas erstellen', playground: 'Playground' },
  en: { heading: 'Start building.', sub: 'No account, no cloud. All data stays local in the browser.', board: 'Create Board', canvas: 'Create Canvas', playground: 'Playground' },
} as const;

export function LandingPage() {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang = i18n.language?.startsWith('en') ? 'en' : 'de';

  useSeoMeta({
    title: 'qtplan – Canvas, Roadmap & Task Planning',
    description: lang === 'de'
      ? 'Lokales Planungstool für Roadmaps, Kanban-Boards, Canvas-Diagramme und PERT-Schätzungen. Kein Account nötig — alle Daten bleiben im Browser.'
      : 'Local-first planning tool for roadmaps, kanban boards, canvas diagrams and PERT estimates. No account needed — all data stays in your browser.',
    lang,
    path: '/',
    alternatePath: '/',
  });
  const cta = midCtaText[lang];
  const demoRef = useRef<HTMLElement>(null);
  const featuresRef = useRef<HTMLElement>(null);
  const heroRef = useRef<HTMLElement>(null);

  // Parallax: hero content fades out / moves up on scroll
  const { scrollY } = useScroll();
  const heroContentY = useTransform(scrollY, [0, 600], [0, -120]);
  const heroContentOpacity = useTransform(scrollY, [0, 400], [1, 0]);

  // Pause WebGL when hero is not in viewport
  const [heroVisible, setHeroVisible] = useState(true);
  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setHeroVisible(entry.isIntersecting),
      { threshold: 0 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Hide top nav when demo section is in view
  const [demoInView, setDemoInView] = useState(false);
  useEffect(() => {
    const el = demoRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setDemoInView(entry.isIntersecting),
      { threshold: 0.1 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const scrollToFeatures = useCallback(() => {
    featuresRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const scrollToDemo = useCallback(() => {
    demoRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  const goToBoards = useCallback(() => {
    navigate({ to: '/boards' });
  }, [navigate]);

  const goToCanvas = useCallback(() => {
    const now = new Date().toISOString();
    const today = `${new Date().getFullYear()}-${String(new Date().getMonth() + 1).padStart(2, '0')}-${String(new Date().getDate()).padStart(2, '0')}`;
    const boardId = generateBoardId('canvas');
    const board = {
      id: boardId,
      name: 'Canvas',
      startDate: today,
      createdAt: now,
      updatedAt: now,
      employeeIds: getEmployeesArray().map(e => e.id),
    };
    boardsStore.send({ type: 'board.create', board });
    navigate({ to: '/boards/$boardId/canvas', params: { boardId } });
  }, [navigate]);

  return (
    <div className="landing-root">
      <LandingTopNav
        onScrollToFeatures={scrollToFeatures}
        onScrollToDemo={scrollToDemo}
        onCreateBoard={goToBoards}
        onCreateCanvas={goToCanvas}
        hidden={demoInView}
      />

      <LandingHero
        heroRef={heroRef}
        heroContentY={heroContentY}
        heroContentOpacity={heroContentOpacity}
        heroVisible={heroVisible}
        goToBoards={goToBoards}
        goToCanvas={goToCanvas}
        scrollToDemo={scrollToDemo}
      />

      <LandingFeatureShowcase featuresRef={featuresRef} onCreateCanvas={goToCanvas} onCreateBoard={goToBoards} />

      {/* Mid-page CTA after features */}
      <section className="landing-mid-cta">
        <h2 className="landing-section-heading">{cta.heading}</h2>
        <p className="landing-section-sub">{cta.sub}</p>
        <div className="landing-mid-cta-actions">
          <button className="landing-btn-primary" onClick={goToBoards}>{cta.board}</button>
          <button className="landing-btn-primary" onClick={goToCanvas}>{cta.canvas}</button>
          <button className="landing-btn-ghost" onClick={scrollToDemo}>
            {cta.playground}
            <ArrowRight size={16} />
          </button>
        </div>
      </section>

      {/* Workflow: Board → Roadmap → Commitment */}
      <LandingWorkflowSection />

      {/* Audience / "Für wen?" section */}
      <LandingAudienceSection />

      <section className="landing-demo-fullscreen" ref={demoRef}>
        <LandingDemo />
      </section>
    </div>
  );
}

export default LandingPage;
